import React, { Fragment, useState } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Table, Badge, Spinner } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import { useGetInventorySnapshotQuery, useGetLowStockReportQuery } from '../../../../store/services/inventoryApi';
import ReactApexChart from 'react-apexcharts';
import { AlertCircle, Package, TrendingUp, DollarSign } from 'react-feather';

const InventoryReporting = () => {
    const [lowStockStoreFilter, setLowStockStoreFilter] = useState('');

    const { data: snapshotData, isLoading: isLoadingSnapshot } = useGetInventorySnapshotQuery({ limit: 2000 });
    const { data: lowStockData, isLoading: isLoadingLowStock } = useGetLowStockReportQuery(
        lowStockStoreFilter ? { storeId: lowStockStoreFilter } : {}
    );

    const snapshot = snapshotData?.data || snapshotData || [];
    const lowStockItems = lowStockData?.data || lowStockData || [];

    // Dashboard Statistics
    const stats = {
        totalItems: snapshot.reduce((acc, item) => acc + (item.quantity || 0), 0),
        totalValuation: snapshot.reduce((acc, item) => acc + ((item.quantity || 0) * (item.productVariantId?.unitCost || 0)), 0),
        lowStockCount: lowStockItems.length,
        uniqueSKUs: snapshot.length
    };

    // ── Store-wise aggregation ─────────────────────────────────────────────
    const storeMap = {};
    snapshot.forEach(item => {
        const name = item.storeId?.storeName || 'Unknown';
        if (!storeMap[name]) storeMap[name] = { _id: item.storeId?._id || '', total: 0, skus: 0, value: 0, products: {} };
        storeMap[name].total += item.quantity || 0;
        storeMap[name].skus  += 1;
        storeMap[name].value += (item.quantity || 0) * (item.productVariantId?.unitCost || 0);
        const pn = item.productVariantId?.productName || 'Unknown';
        storeMap[name].products[pn] = (storeMap[name].products[pn] || 0) + (item.quantity || 0);
    });

    const storeNames  = Object.keys(storeMap);
    const storeTotals = storeNames.map(n => storeMap[n].total);
    const grandTotal  = storeTotals.reduce((a, b) => a + b, 0) || 1;

    const PALETTE = ['#d3178a', '#f8d62b', '#51bb25', '#a927f9', '#fb740d', '#1ea6ec', '#f73164'];

    // Horizontal bar chart — total units per store
    const barChartOptions = {
        chart: { type: 'bar', toolbar: { show: false }, background: 'transparent' },
        plotOptions: {
            bar: {
                horizontal: true,
                borderRadius: 6,
                distributed: true,
                dataLabels: { position: 'center' }
            }
        },
        dataLabels: {
            enabled: true,
            formatter: val => val.toLocaleString(),
            offsetX: 0,
            style: { fontSize: '12px', fontWeight: 700, colors: ['#fff'] }
        },
        colors: PALETTE,
        xaxis: { categories: storeNames, labels: { style: { fontSize: '12px' } } },
        yaxis: { labels: { style: { fontSize: '12px', fontWeight: 600 } } },
        legend: { show: false },
        grid: { borderColor: '#f1f1f1', xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
        tooltip: { y: { formatter: val => `${val.toLocaleString()} units` } }
    };

    const barChartSeries = [{ name: 'Units in Stock', data: storeTotals }];

    // Donut — share of total stock per store
    const donutOptions = {
        chart: { type: 'donut' },
        labels: storeNames,
        colors: PALETTE,
        plotOptions: { pie: { donut: { size: '70%', labels: { show: true, total: { show: true, label: 'Total Units', formatter: () => grandTotal.toLocaleString() } } } } },
        legend: { position: 'bottom', fontSize: '13px' },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: val => `${val.toLocaleString()} units` } }
    };

    if (isLoadingSnapshot || isLoadingLowStock) return <div className="text-center p-5"><Spinner color="primary" /></div>;

    return (
        <Fragment>
            <Breadcrumbs mainTitle="Inventory Intelligence" parent="Inventory" title="Reporting & Analytics" />
            <Container fluid={true}>
                <Row>
                    {/* Stat Cards */}
                    <Col sm="6" xl="3">
                        <Card className="widget-1">
                            <CardBody>
                                <div className="widget-content">
                                    <div className="widget-round secondary"><Package size={20} /></div>
                                    <div>
                                        <h4>{stats.totalItems.toLocaleString()}</h4>
                                        <span className="f-light">Total Units in Stock</span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col sm="6" xl="3">
                        <Card className="widget-1">
                            <CardBody>
                                <div className="widget-content">
                                    <div className="widget-round success"><TrendingUp size={20} /></div>
                                    <div>
                                        <h4>{stats.uniqueSKUs}</h4>
                                        <span className="f-light">Unique Product SKUs</span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col sm="6" xl="3">
                        <Card className="widget-1">
                            <CardBody>
                                <div className="widget-content">
                                    <div className="widget-round warning"><AlertCircle size={20} /></div>
                                    <div>
                                        <h4 className="text-danger">{stats.lowStockCount}</h4>
                                        <span className="f-light">Low Stock Alerts</span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col sm="6" xl="3">
                        <Card className="widget-1">
                            <CardBody>
                                <div className="widget-content">
                                    <div className="widget-round info"><DollarSign size={20} /></div>
                                    <div>
                                        <h4>${stats.totalValuation.toLocaleString()}</h4>
                                        <span className="f-light">Estimated Inventory Value</span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* ── Stock Distribution by Store ── */}
                <Row>
                    <Col xl="8">
                        <Card>
                            <CardHeader>
                                <h5>Stock Distribution by Store</h5>
                                <small className="text-muted">Total units on hand per location</small>
                            </CardHeader>
                            <CardBody>
                                {storeNames.length > 0
                                    ? <ReactApexChart options={barChartOptions} series={barChartSeries} type="bar" height={Math.max(250, storeNames.length * 52)} />
                                    : <p className="text-center text-muted py-4">No store data available.</p>
                                }
                            </CardBody>
                        </Card>
                    </Col>

                    <Col xl="4">
                        <Card>
                            <CardHeader>
                                <h5>Share of Total Stock</h5>
                                <small className="text-muted">% contribution per store</small>
                            </CardHeader>
                            <CardBody>
                                {storeNames.length > 0
                                    ? <ReactApexChart options={donutOptions} series={storeTotals} type="donut" height={300} />
                                    : <p className="text-center text-muted py-4">No store data available.</p>
                                }
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* ── Per-Store Summary Cards ── */}
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <h5>Per-Store Breakdown</h5>
                                <Badge color="primary">{storeNames.length} Store{storeNames.length !== 1 ? 's' : ''}</Badge>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    {storeNames.map((store, idx) => {
                                        const info     = storeMap[store];
                                        const pct      = Math.round((info.total / grandTotal) * 100);
                                        const color    = PALETTE[idx % PALETTE.length];
                                        const topProds = Object.entries(info.products)
                                            .sort((a, b) => b[1] - a[1])
                                            .slice(0, 4);

                                        return (
                                            <Col key={store} sm="6" xl="4" className="mb-3">
                                                <div style={{
                                                    border: `1px solid ${color}33`,
                                                    borderRadius: '12px',
                                                    padding: '16px',
                                                    background: `linear-gradient(135deg, ${color}08, transparent)`
                                                }}>
                                                    {/* Store header */}
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <div>
                                                            <div className="fw-bold" style={{ fontSize: '14px' }}>{store}</div>
                                                            <small className="text-muted">{info.skus} SKUs</small>
                                                        </div>
                                                        <div className="text-end">
                                                            <div className="fw-bold" style={{ fontSize: '20px', color }}>{info.total.toLocaleString()}</div>
                                                            <small className="text-muted">units</small>
                                                        </div>
                                                    </div>

                                                    {/* Share progress bar */}
                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between mb-1">
                                                            <small className="text-muted">Share of total stock</small>
                                                            <small className="fw-bold" style={{ color }}>{pct}%</small>
                                                        </div>
                                                        <div style={{ height: '6px', background: '#eee', borderRadius: '4px', overflow: 'hidden' }}>
                                                            <div style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: '4px', transition: 'width 0.6s ease' }} />
                                                        </div>
                                                    </div>

                                                    {/* Valuation */}
                                                    <div className="mb-3 p-2" style={{ background: `${color}12`, borderRadius: '8px' }}>
                                                        <small className="text-muted d-block">Est. Value</small>
                                                        <span className="fw-bold" style={{ fontSize: '13px' }}>${info.value.toLocaleString()}</span>
                                                    </div>

                                                    {/* Top products */}
                                                    {topProds.length > 0 && (
                                                        <div>
                                                            <small className="text-muted fw-semibold d-block mb-1" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Top Products</small>
                                                            {topProds.map(([pName, qty]) => {
                                                                const prodPct = Math.round((qty / info.total) * 100);
                                                                return (
                                                                    <div key={pName} className="mb-1">
                                                                        <div className="d-flex justify-content-between">
                                                                            <small className="text-truncate" style={{ maxWidth: '65%', fontSize: '11px' }} title={pName}>{pName}</small>
                                                                            <small className="fw-bold" style={{ fontSize: '11px' }}>{qty} <span className="text-muted">({prodPct}%)</span></small>
                                                                        </div>
                                                                        <div style={{ height: '3px', background: '#eee', borderRadius: '2px', overflow: 'hidden' }}>
                                                                            <div style={{ width: `${prodPct}%`, background: `${color}99`, height: '100%', borderRadius: '2px' }} />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </Col>
                                        );
                                    })}

                                    {storeNames.length === 0 && (
                                        <Col sm="12">
                                            <p className="text-center text-muted py-4">No store inventory data available.</p>
                                        </Col>
                                    )}
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    <Col xl="7">
                        <Card>
                            <CardHeader>
                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                    <div>
                                        <h5 className="mb-0">Urgent Low Stock Warnings</h5>
                                        <small className="text-muted">Products at or below safety stock level</small>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <select
                                            id="low-stock-store-filter"
                                            value={lowStockStoreFilter}
                                            onChange={e => setLowStockStoreFilter(e.target.value)}
                                            style={{
                                                border: '1px solid #e0e0e0',
                                                borderRadius: '8px',
                                                padding: '5px 10px',
                                                fontSize: '13px',
                                                outline: 'none',
                                                background: '#fff',
                                                cursor: 'pointer',
                                                minWidth: '150px'
                                            }}
                                        >
                                            <option value="">All Stores</option>
                                            {storeNames.map(name => (
                                                <option key={name} value={storeMap[name]._id || name}>
                                                    {name}
                                                </option>
                                            ))}
                                        </select>
                                        <Badge color="danger">{lowStockItems.length} Items</Badge>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    <Table bordered hover size="sm">
                                        <thead className="table-light sticky-top">
                                            <tr>
                                                <th>Product</th>
                                                <th>Store</th>
                                                <th>Current Stock</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {lowStockItems.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        <div className="fw-bold">{item.productVariantId?.productName}</div>
                                                        <small className="text-muted">SKU: {item.productVariantId?.sku}</small>
                                                    </td>
                                                    <td>{item.storeId?.storeName}</td>
                                                    <td className="text-center font-danger fw-bold">{item.quantity}</td>
                                                    <td className="text-center">
                                                        <Badge color="danger">Critical</Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                            {lowStockItems.length === 0 && (
                                                <tr>
                                                    <td colSpan="4" className="text-center p-4 text-muted">All stock levels are healthy!</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col xl="5">
                        <Card>
                            <CardHeader><h5>Store Summary</h5></CardHeader>
                            <CardBody className="p-0">
                                <Table borderless className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Store</th>
                                            <th className="text-center">SKUs</th>
                                            <th className="text-center">Units</th>
                                            <th className="text-end">Est. Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {storeNames
                                            .sort((a, b) => storeMap[b].total - storeMap[a].total)
                                            .map((store, idx) => (
                                                <tr key={store}>
                                                    <td>
                                                        <span style={{
                                                            display: 'inline-block', width: '10px', height: '10px',
                                                            borderRadius: '50%', background: PALETTE[idx % PALETTE.length],
                                                            marginRight: '8px'
                                                        }} />
                                                        {store}
                                                    </td>
                                                    <td className="text-center">{storeMap[store].skus}</td>
                                                    <td className="text-center fw-bold">{storeMap[store].total.toLocaleString()}</td>
                                                    <td className="text-end">${storeMap[store].value.toLocaleString()}</td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </Table>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader><h5>Full Inventory Snapshot</h5></CardHeader>
                            <CardBody>
                                <div className="table-responsive">
                                    <Table hover striped bordered className="text-center align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th>SKU</th>
                                                <th className="text-start">Product Variant</th>
                                                <th>Store</th>
                                                <th>Qty on Hand</th>
                                                <th>Unit Cost</th>
                                                <th>Value</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {snapshot.map((row, i) => (
                                                <tr key={i}>
                                                    <td>{row.productVariantId?.sku}</td>
                                                    <td className="text-start">
                                                        <div className="fw-bold">{row.productVariantId?.productName}</div>
                                                        <small className="text-muted">{row.productVariantId?.variantName}</small>
                                                    </td>
                                                    <td>{row.storeId?.storeName}</td>
                                                    <td className={row.quantity <= 10 ? 'text-danger fw-bold' : ''}>{row.quantity}</td>
                                                    <td>${row.productVariantId?.unitCost || 0}</td>
                                                    <td className="fw-bold">${((row.quantity || 0) * (row.productVariantId?.unitCost || 0)).toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default InventoryReporting;
